// NOTIFY-01 live updates for the admin: a single-use ticket (POST …/realtime/ticket) opens a
// WebSocket to RealtimeHubDO through ady-api; if that fails the next ticket opens SSE; if that fails
// too (or the server answers activation_required) the bell and the centre poll every 30 s. A socket
// the hub closes for its lifetime (4001) or a membership change (4003) reconnects with a fresh ticket.
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { i18n } from "@ady/i18n";
import type { MeMembership } from "@ady/contracts";
import { notificationsApi, notifyKeys, type AppNotification } from "./api";

export type Transport = "websocket" | "sse" | "poll";
export type LiveState = "connecting" | "live" | "polling";

export const POLL_MS = 30_000;
const RECONNECT_MS = 2_000;

/** The fallback order: WebSocket → SSE → polling (never back up without a fresh start). */
export function nextTransport(t: Transport): Transport {
  return t === "websocket" ? "sse" : "poll";
}

/** `wss://host/api/v1/…` from the page origin and the ticket's same-origin path. */
export function wsUrl(origin: string, path: string): string {
  const u = new URL(path, origin);
  u.protocol = u.protocol === "http:" ? "ws:" : "wss:";
  return u.toString();
}

/** The bell's badge text: nothing at 0, the count up to 9, then "9+". */
export function badgeText(count: number): string | null {
  if (count <= 0) return null;
  return count > 9 ? i18n.t("notifications.bell.many") : String(count);
}

/** The title in English from the stored key + params (D-013; the server never sends text). */
export function titleOf(n: Pick<AppNotification, "title_key" | "params">): string {
  return i18n.tDynamic(n.title_key, "notifications.events.fallback.title", n.params);
}

/** Server links are `/s/<store id>/<rest>`; the admin's paths use the org and store slugs. */
export function resolveLink(link: string | null, m: MeMembership): string | null {
  if (!link) return null;
  const match = /^\/s\/(str_[0-9A-HJKMNP-TV-Z]{26})(\/.*)?$/.exec(link);
  if (match) {
    const store = m.stores.find((s) => s.id === match[1]);
    return store ? `/${m.org.slug}/${store.slug}${match[2] ?? ""}` : null;
  }
  if (link === "/notifications") {
    const first = m.stores[0];
    return first ? `/${m.org.slug}/${first.slug}/notifications` : null;
  }
  return null;
}

/** Merge a live notification into a cached page (newest first, no duplicates). */
export function mergeLive(list: AppNotification[], n: AppNotification): AppNotification[] {
  return [n, ...list.filter((x) => x.id !== n.id)];
}

/**
 * Keeps the bell and the centre fresh. Returns the state so the UI can say "Live" or
 * "Updates every 30 seconds"; in `polling` the queries refetch every POLL_MS.
 */
export function useLiveNotifications(orgId: string): LiveState {
  const qc = useQueryClient();
  const [state, setState] = useState<LiveState>("connecting");
  useEffect(() => {
    let stopped = false;
    let ws: WebSocket | null = null;
    let es: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => qc.invalidateQueries({ queryKey: notifyKeys.all(orgId) });
    const onPayload = (raw: string) => {
      try {
        const m = JSON.parse(raw) as { type?: string };
        if (m.type === "notification.created") void refresh();
      } catch {
        // ignore malformed frames
      }
    };
    const open = async (t: Transport): Promise<void> => {
      if (stopped) return;
      if (t === "poll" || typeof window === "undefined") {
        setState("polling");
        return;
      }
      let ticket;
      try {
        ticket = await notificationsApi.ticket(orgId);
      } catch {
        setState("polling"); // activation_required (no hub / no key) or offline: poll truthfully
        return;
      }
      if (stopped) return;
      if (t === "websocket" && typeof WebSocket !== "undefined") {
        let opened = false;
        ws = new WebSocket(wsUrl(window.location.origin, ticket.url));
        ws.onopen = () => {
          opened = true;
          setState("live");
        };
        ws.onmessage = (e) => onPayload(String(e.data));
        ws.onclose = (e) => {
          ws = null;
          if (stopped) return;
          if (
            opened &&
            (e.code === 4001 || e.code === 4003 || e.code === 1006 || e.code === 1001)
          ) {
            timer = setTimeout(() => void open("websocket"), RECONNECT_MS);
          } else if (!opened) void open(nextTransport("websocket"));
          else setState("polling");
        };
        return;
      }
      if (typeof EventSource === "undefined") {
        setState("polling");
        return;
      }
      es = new EventSource(ticket.url, { withCredentials: true });
      let gotAny = false;
      es.onmessage = (e) => {
        if (!gotAny) setState("live");
        gotAny = true;
        onPayload(String(e.data));
      };
      es.onerror = () => {
        es?.close();
        es = null;
        if (stopped) return;
        // The hub ends an SSE stream after ~50 s: reconnect while it worked, else poll.
        if (gotAny) timer = setTimeout(() => void open("sse"), RECONNECT_MS);
        else setState("polling");
      };
    };
    void open("websocket");
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      ws?.close(1000);
      es?.close();
    };
  }, [orgId, qc]);
  return state;
}
